// Client-side store for per-member capability overrides.
//
// Wave 19 introduces the `org_member_capabilities` table (migration 0020).
// This module is the single read/write surface for it. The public hook
// (`useCapabilities`) pulls the current user's overrides via OrgProvider;
// the admin permissions-matrix UI (wave 22) will call list*() + set*() to
// manage other members.
//
// Dual-mode: when VITE_E2E_BYPASS_AUTH=true or Supabase isn't configured
// we back the store with localStorage so Playwright (and local dev without
// a `.env.local`) stays functional. The storage shape is a flat array of
// CapabilityOverride rows under `proofly.bypass.capability_overrides.v1`.

import { isSupabaseConfigured, supabase } from "../supabase";
import type { Capability } from "./capabilities";

export interface CapabilityOverride {
  organizationId: string;
  userId: string;
  capability: Capability;
  granted: boolean;
  grantedBy: string | null;
  grantedAt: string;
}

const BYPASS_KEY = "proofly.bypass.capability_overrides.v1";

const useLocal =
  import.meta.env.VITE_E2E_BYPASS_AUTH === "true" || !isSupabaseConfigured;

// ---------------------------------------------------------------------------
// localStorage backend — mirrors the schema 1:1 so the bypass path and the
// supabase path are interchangeable from the caller's perspective.
// ---------------------------------------------------------------------------

interface StoredOverride {
  organizationId: string;
  userId: string;
  capability: string;
  granted: boolean;
  grantedBy: string | null;
  grantedAt: string;
}

function readBypass(): StoredOverride[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(BYPASS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as StoredOverride[];
  } catch {
    return [];
  }
}

function writeBypass(rows: StoredOverride[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(BYPASS_KEY, JSON.stringify(rows));
  } catch {
    /* swallow */
  }
}

function toOverride(row: StoredOverride): CapabilityOverride {
  return {
    organizationId: row.organizationId,
    userId: row.userId,
    capability: row.capability as Capability,
    granted: row.granted,
    grantedBy: row.grantedBy,
    grantedAt: row.grantedAt,
  };
}

// ---------------------------------------------------------------------------
// Supabase row <-> domain mapping.
// ---------------------------------------------------------------------------

interface SupabaseOverrideRow {
  organization_id: string;
  user_id: string;
  capability: string;
  granted: boolean;
  granted_by: string | null;
  granted_at: string;
}

function rowToOverride(row: SupabaseOverrideRow): CapabilityOverride {
  return {
    organizationId: row.organization_id,
    userId: row.user_id,
    capability: row.capability as Capability,
    granted: row.granted,
    grantedBy: row.granted_by,
    grantedAt: row.granted_at,
  };
}

const COLS = "organization_id, user_id, capability, granted, granted_by, granted_at";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// Read all overrides for an org (admin/owner use case — e.g. the
// permissions-matrix page in wave 22).
export async function listOverridesForOrg(
  orgId: string,
): Promise<CapabilityOverride[]> {
  if (useLocal) {
    return readBypass()
      .filter((r) => r.organizationId === orgId)
      .map(toOverride);
  }
  const { data, error } = await supabase
    .from("org_member_capabilities")
    .select(COLS)
    .eq("organization_id", orgId);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as unknown as SupabaseOverrideRow[];
  return rows.map(rowToOverride);
}

// Read overrides for a specific member. Used by OrgProvider to load the
// current user's own overrides at session-start.
export async function listOverridesForMember(
  orgId: string,
  userId: string,
): Promise<CapabilityOverride[]> {
  if (useLocal) {
    return readBypass()
      .filter((r) => r.organizationId === orgId && r.userId === userId)
      .map(toOverride);
  }
  const { data, error } = await supabase
    .from("org_member_capabilities")
    .select(COLS)
    .eq("organization_id", orgId)
    .eq("user_id", userId);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as unknown as SupabaseOverrideRow[];
  return rows.map(rowToOverride);
}

// Upsert a grant or revoke. The server enforces ownership semantics via
// RLS (see 0020_org_member_capabilities.sql / can_override_capability).
export async function setOverride(
  orgId: string,
  userId: string,
  capability: Capability,
  granted: boolean,
): Promise<void> {
  if (useLocal) {
    const rows = readBypass();
    const idx = rows.findIndex(
      (r) =>
        r.organizationId === orgId &&
        r.userId === userId &&
        r.capability === capability,
    );
    const now = new Date().toISOString();
    if (idx === -1) {
      rows.push({
        organizationId: orgId,
        userId,
        capability,
        granted,
        grantedBy: null,
        grantedAt: now,
      });
    } else {
      rows[idx] = { ...rows[idx]!, granted, grantedAt: now };
    }
    writeBypass(rows);
    return;
  }
  const { error } = await supabase.from("org_member_capabilities").upsert(
    {
      organization_id: orgId,
      user_id: userId,
      capability,
      granted,
    },
    { onConflict: "organization_id,user_id,capability" },
  );
  if (error) throw new Error(error.message);
}

// Delete an override row (falls back to role default).
export async function clearOverride(
  orgId: string,
  userId: string,
  capability: Capability,
): Promise<void> {
  if (useLocal) {
    const rows = readBypass().filter(
      (r) =>
        !(
          r.organizationId === orgId &&
          r.userId === userId &&
          r.capability === capability
        ),
    );
    writeBypass(rows);
    return;
  }
  const { error } = await supabase
    .from("org_member_capabilities")
    .delete()
    .eq("organization_id", orgId)
    .eq("user_id", userId)
    .eq("capability", capability);
  if (error) throw new Error(error.message);
}
